import React, { useState, useRef, useEffect } from "react";
import { Mic } from "lucide-react";
import { FaSquare, FaPlay, FaPause } from "react-icons/fa";

const FutureNoteStep = ({
  futureVoiceNote,
  onVoiceUpload,
  uploading,
  onRecordingChange,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentPlayTime, setCurrentPlayTime] = useState(0);
  const [audioURL, setAudioURL] = useState(futureVoiceNote || null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showRedoConfirm, setShowRedoConfirm] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // keep local preview in sync with parent
  useEffect(() => {
    setAudioURL(futureVoiceNote || null);
  }, [futureVoiceNote]);

  useEffect(() => {
    if (!audioRef.current || !audioURL) return;

    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      setAudioDuration(Math.floor(audio.duration || 0));
    };

    const handleTimeUpdate = () => {
      setCurrentPlayTime(Math.floor(audio.currentTime));
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audioURL]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        await onVoiceUpload?.(audioBlob);
        stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);
        onRecordingChange?.(false);
      };

      mediaRecorder.start();
      setIsRecording(true);
      onRecordingChange?.(true);
      setRecordingTime(0);
      timerRef.current = setInterval(
        () => setRecordingTime((p) => p + 1),
        1000
      );
    } catch (err) {
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      onRecordingChange?.(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const redoRecording = () => {
    setShowRedoConfirm(true);
  };

  const confirmRedo = () => {
    setAudioURL(null);
    setRecordingTime(0);
    setAudioDuration(0);
    setCurrentPlayTime(0);
    setIsPlaying(false);
    onVoiceUpload?.(null);
    onRecordingChange?.(false);
    setShowRedoConfirm(false);
  };

  const cancelRedo = () => {
    setShowRedoConfirm(false);
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds) => {
    const secs = Number(seconds || 0);
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return `${mins}:${rem.toString().padStart(2, "0")}`;
  };

  return (
    <div>
      <p className="font-bold text-lg md:text-[18px] text-[#323232] font-gotham">
        As we wrap up, take a moment to look forward.
      </p>
      <p className="text-lg md:text-[18px] font-gotham font-bold text-[#323232] mb-6">
        What message would you like to leave for your future self?
      </p>

      {/* 1. Record CTA */}
      {!audioURL && !isRecording && !uploading && (
        <div
          onClick={startRecording}
          className="border-2 border-dashed border-gray-300 rounded-lg h-48 flex flex-col items-center justify-center hover:border-teal-400 transition-all hover:bg-[#D3D0D0] cursor-pointer"
        >
          <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-4">
            <Mic className="w-8 h-8 text-white" />
          </div>
          <p className="text-sm text-gray-600 font-medium">
            Click to record voice note
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Tap the microphone to start
          </p>
        </div>
      )}

      {/* 2. Recording UI */}
      {isRecording && (
        <div className="border-2 border-teal-500 rounded-lg p-8 bg-teal-50">
          <div className="flex flex-col items-center justify-center">
            <div className="relative mb-6">
              <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center animate-pulse">
                <Mic className="w-10 h-10 text-white" />
              </div>
              <div className="absolute inset-0 w-20 h-20 bg-teal-500 rounded-full animate-ping opacity-20" />
            </div>

            <div className="mb-6">
              <p className="text-4xl font-bold text-teal-600 font-mono">
                {formatTime(recordingTime)}
              </p>
            </div>

            <button
              onClick={stopRecording}
              className="flex items-center gap-2 px-8 py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all font-semibold"
            >
              <FaSquare className="w-5 h-5" />
              <span>Stop Recording</span>
            </button>
          </div>
        </div>
      )}

      {/* 3. Uploading */}
      {uploading && (
        <div className="border-2 border-gray-300 rounded-lg p-8 bg-gray-50">
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm text-teal-600 font-medium">
              Uploading to cloud...
            </p>
          </div>
        </div>
      )}

      {/* 4. Player */}
      {audioURL && !uploading && (
        <div className="border-2 border-gray-300 rounded-lg p-6 bg-[#F1F1F1] max-w-xl">
          <div className="flex items-center gap-4 mb-1">
            <button
              onClick={togglePlayPause}
              className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center hover:bg-teal-600 transition-all"
            >
              {isPlaying ? (
                <FaPause className="w-6 h-6 text-white" />
              ) : (
                <FaPlay className="w-6 h-6 text-white ml-1" />
              )}
            </button>

            <div className="flex-1">
              <div className="h-12 bg-[#F1F1F1] rounded-lg flex items-center px-4">
                <div className="flex gap-1 items-center justify-center w-full">
                  {Array.from({ length: 40 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-1 bg-[#C4C1C1] rounded-full"
                      style={{
                        height: `${Math.random() * 24 + 8}px`,
                        opacity: isPlaying ? 0.8 : 0.4,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <span className="text-sm md:text-2xl text-[#666666] font-bold font-gotham min-w-[40px]">
              {formatTime(isPlaying ? currentPlayTime : audioDuration)}
            </span>
          </div>

          <audio
            ref={audioRef}
            src={audioURL}
            preload="metadata"
            onLoadedMetadata={(e) => {
              const duration = Math.floor(e.currentTarget.duration || 0);
              setAudioDuration(duration);
            }}
            onTimeUpdate={(e) => {
              setCurrentPlayTime(Math.floor(e.currentTarget.currentTime));
            }}
            onEnded={() => {
              setIsPlaying(false);
              setCurrentPlayTime(0);
            }}
            className="hidden"
          />
        </div>
      )}

      {/* 5. Redo */}
      <button
        onClick={redoRecording}
        className="w-full text-start text-sm md:text-[12px] text-[#D3D0D0] hover:text-[#54B0AF] font-gotham font-bold uppercase tracking-wide mt-4"
      >
        REDO NOTE
      </button>

      {/* Redo Confirmation Modal */}
      {showRedoConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-gotham font-semibold text-[#323232] mb-4">
              Delete Recording?
            </h3>
            <p className="text-sm font-gotham text-[#666666] mb-6">
              Are you sure you want to delete your current voice note? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelRedo}
                className="px-6 py-2 border-2 border-gray-300 rounded-lg font-gotham font-medium text-[#323232] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRedo}
                className="px-6 py-2 bg-red-500 text-white rounded-lg font-gotham font-medium hover:bg-red-600 transition-colors"
              >
                Delete & Redo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FutureNoteStep;